import { CUSTOMER_LEVEL } from '@wyz/types';
import { Flex } from '@wyz/ui';
import {
  useManageCustomers,
  useManageParameters,
  useManageVehicleTypeGroups,
} from '~/common/api';
import { Trans, useI18n } from '~/common/context';
import { IUserCustomer } from '~/common/types';
import { ListTooltip, SearchValuePreview } from '~/purchase/components';
import { PurchaseSearchHelpers } from '~/purchase/helpers';
import { SEARCH_TYPE_ENUM } from '~/purchase/schemas';
import { IBaseSearchFormSchema } from '~/purchase/types';

function SearchPreview({
  customer,
  searchQuery,
}: {
  customer: IUserCustomer;
  searchQuery: IBaseSearchFormSchema | undefined;
}) {
  const { t } = useI18n();
  const manageParameters = useManageParameters({
    getSeasons: {
      enabled: true,
    },
  });
  const manageVehicleTypeGroups = useManageVehicleTypeGroups({
    coreVehicleTypeGroup: { enabled: true },
  });

  const manageCustomers = useManageCustomers({
    getBrands: {
      args: {
        customerPublicKey: customer.id,
        ctx:
          customer.level === CUSTOMER_LEVEL.N1
            ? SEARCH_TYPE_ENUM.DISTRIBUTION
            : SEARCH_TYPE_ENUM.REDISTRIBUTION,
      },
    },
  });

  const seasonLabels = manageParameters.getSeasons.data?.data
    .filter((item) => searchQuery?.seasons?.includes(item.publicKey))
    .map((item) => t(item.i18nKey));
  const brandLabels = manageCustomers.getBrands.data?.data
    .filter((item) => searchQuery?.brands?.includes(item.publicKey))
    .map((item) => item.name);
  const showLoad =
    searchQuery?.loadIndexes && searchQuery?.loadIndexes.length === 1;
  const showSpeed =
    showLoad &&
    searchQuery?.speedIndexes &&
    searchQuery?.speedIndexes.length === 1;
  const vehicleGroup =
    manageVehicleTypeGroups.coreVehicleTypeGroup.data?.data.find(
      (group) => group.name === searchQuery?.vehicleTypeGroup,
    );
  return (
    <Flex className='space-x-4 w-100' justifyContent='end' wrap='wrap'>
      <SearchValuePreview
        label={<Trans i18nKey='app.purchase.search_page.vehicle_type' />}
        value={
          searchQuery?.vehicleTypeGroup
            ? vehicleGroup
              ? t(vehicleGroup.i18nKey, { defaultValue: vehicleGroup.name })
              : searchQuery?.vehicleTypeGroup
            : undefined
        }
      />
      <SearchValuePreview
        label={<Trans i18nKey='app.purchase.search_page.selected_dimension' />}
        value={PurchaseSearchHelpers.formatDimension({
          width: searchQuery?.width,
          height: searchQuery?.height,
          diameter: searchQuery?.diameter,
          loadIndex: showLoad ? searchQuery?.loadIndexes?.[0] : undefined,
          speedIndex: showSpeed ? searchQuery?.speedIndexes?.[0] : undefined,
          identifier: searchQuery?.identifier?.label,
        })}
      />
      <SearchValuePreview
        label={<Trans i18nKey='app.common.product.season' />}
        value={
          seasonLabels?.length
            ? seasonLabels?.join(',')
            : t('app.common.all_female')
        }
      />
      <SearchValuePreview
        label={<Trans i18nKey='app.common.product.brands' />}
        value={
          brandLabels?.length ? (
            <ListTooltip list={brandLabels} />
          ) : (
            t('app.common.all_female')
          )
        }
      />
      <SearchValuePreview
        label={<Trans i18nKey='app.common.quantity' />}
        value={searchQuery?.quantity}
      />
    </Flex>
  );
}

export default SearchPreview;
